import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const meseCorrente = () => new Date().toISOString().slice(0, 7);

const VUOTO = {
  nome: '',
  beneficiario: '',
  categoria: 'AFFITTO',
  importo: '',
  cadenza: 'mensile',
  mese_inizio: meseCorrente(),
  fine_modo: 'senza_fine',
  mese_fine: '',
  n_rate: '',
  giorno_scadenza: '1',
  metodo_pagamento: 'bonifico',
  con_fattura: false,
  note: '',
  attivo: true,
};

const daCosto = (c) => ({
  nome: c.nome || '',
  beneficiario: c.beneficiario || '',
  categoria: c.categoria || 'AFFITTO',
  importo: String(c.importo ?? ''),
  cadenza: c.cadenza || 'mensile',
  mese_inizio: c.mese_inizio || meseCorrente(),
  fine_modo: c.senza_fine ? 'senza_fine' : (c.n_rate ? 'rate' : 'scadenza'),
  mese_fine: c.mese_fine || '',
  n_rate: c.n_rate ? String(c.n_rate) : '',
  giorno_scadenza: c.giorno_scadenza ? String(c.giorno_scadenza) : '',
  metodo_pagamento: c.metodo_pagamento || 'bonifico',
  con_fattura: !!c.con_fattura,
  note: c.note || '',
  attivo: c.attivo !== false,
});

/** Form di inserimento/modifica di un costo fisso ricorrente. */
export const CostoFissoForm = ({ aperto, onChiudi, costo, opzioni, onSalva, salvando }) => {
  const [f, setF] = useState(VUOTO);

  useEffect(() => {
    if (aperto) setF(costo ? daCosto(costo) : VUOTO);
  }, [aperto, costo]);

  const set = (campo) => (v) => setF((p) => ({ ...p, [campo]: v }));
  const unaTantum = f.cadenza === 'una_tantum';

  const invia = () => {
    onSalva({
      nome: f.nome.trim(),
      beneficiario: f.beneficiario.trim(),
      categoria: f.categoria,
      importo: Number(String(f.importo).replace(',', '.')),
      cadenza: f.cadenza,
      mese_inizio: f.mese_inizio,
      senza_fine: !unaTantum && f.fine_modo === 'senza_fine',
      mese_fine: !unaTantum && f.fine_modo === 'scadenza' ? f.mese_fine : null,
      n_rate: !unaTantum && f.fine_modo === 'rate' ? Number(f.n_rate) : null,
      giorno_scadenza: f.giorno_scadenza ? Number(f.giorno_scadenza) : null,
      metodo_pagamento: f.metodo_pagamento,
      con_fattura: f.con_fattura,
      note: f.note,
      attivo: f.attivo,
    });
  };

  const valido =
    f.nome.trim() &&
    Number(String(f.importo).replace(',', '.')) > 0 &&
    /^\d{4}-\d{2}$/.test(f.mese_inizio) &&
    (unaTantum ||
      f.fine_modo === 'senza_fine' ||
      (f.fine_modo === 'scadenza' && /^\d{4}-\d{2}$/.test(f.mese_fine)) ||
      (f.fine_modo === 'rate' && Number(f.n_rate) >= 1));

  return (
    <Dialog open={aperto} onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="costo-fisso-form">
        <DialogHeader>
          <DialogTitle>{costo ? 'Modifica costo fisso' : 'Nuovo costo fisso'}</DialogTitle>
          <DialogDescription className="text-xs">
            Costi ricorrenti senza fattura elettronica: indica quanto esce davvero per ogni rata
            (IVA inclusa).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nome del costo</Label>
            <Input
              value={f.nome}
              onChange={(e) => set('nome')(e.target.value)}
              placeholder="Affitto locale, Rata furgone…"
              data-testid="costo-fisso-nome"
            />
          </div>

          <div>
            <Label className="text-xs">Beneficiario / controparte</Label>
            <Input
              value={f.beneficiario}
              onChange={(e) => set('beneficiario')(e.target.value)}
              placeholder="Proprietario, banca, società di leasing…"
              data-testid="costo-fisso-beneficiario"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Importo rata (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={f.importo}
                onChange={(e) => set('importo')(e.target.value)}
                className="tabular-nums"
                data-testid="costo-fisso-importo"
              />
            </div>
            <div>
              <Label className="text-xs">Categoria</Label>
              <Select value={f.categoria} onValueChange={set('categoria')}>
                <SelectTrigger data-testid="costo-fisso-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(opzioni.categorie || []).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Cadenza</Label>
              <Select value={f.cadenza} onValueChange={set('cadenza')}>
                <SelectTrigger data-testid="costo-fisso-cadenza"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(opzioni.cadenze || []).map((c) => (
                    <SelectItem key={c.valore} value={c.valore}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Prima scadenza</Label>
              <Input
                type="month"
                value={f.mese_inizio}
                onChange={(e) => set('mese_inizio')(e.target.value)}
                data-testid="costo-fisso-mese-inizio"
              />
            </div>
          </div>

          {!unaTantum && (
            <div className="space-y-3 rounded-lg border p-3 bg-secondary/40">
              <div>
                <Label className="text-xs">Durata del piano</Label>
                <Select value={f.fine_modo} onValueChange={set('fine_modo')}>
                  <SelectTrigger data-testid="costo-fisso-fine-modo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="senza_fine">Senza fine (ricorrenza indeterminata)</SelectItem>
                    <SelectItem value="scadenza">Fino a una data (ultima scadenza)</SelectItem>
                    <SelectItem value="rate">Numero di rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {f.fine_modo === 'scadenza' && (
                <div>
                  <Label className="text-xs">Ultima scadenza</Label>
                  <Input
                    type="month"
                    value={f.mese_fine}
                    onChange={(e) => set('mese_fine')(e.target.value)}
                    data-testid="costo-fisso-mese-fine"
                  />
                </div>
              )}
              {f.fine_modo === 'rate' && (
                <div>
                  <Label className="text-xs">Numero totale di rate</Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    value={f.n_rate}
                    onChange={(e) => set('n_rate')(e.target.value)}
                    data-testid="costo-fisso-n-rate"
                  />
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Indica l'ultima scadenza oppure il numero di rate: l'altro dato viene calcolato
                e sulle rate vedrai «rata 14 di 36».
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Giorno di scadenza</Label>
              <Input
                type="number"
                min="1"
                max="31"
                step="1"
                inputMode="numeric"
                value={f.giorno_scadenza}
                onChange={(e) => set('giorno_scadenza')(e.target.value)}
                placeholder="1"
                data-testid="costo-fisso-giorno"
              />
            </div>
            <div>
              <Label className="text-xs">Pagamento</Label>
              <Select value={f.metodo_pagamento} onValueChange={set('metodo_pagamento')}>
                <SelectTrigger data-testid="costo-fisso-metodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(opzioni.metodi_pagamento || []).map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
            <div className="min-w-0">
              <Label className="text-xs">Arriva anche una fattura</Label>
              <p className="text-[11px] text-muted-foreground">
                Se questo costo è documentato da fattura elettronica lo segnalo come possibile
                doppione ogni volta che trovo un importo simile.
              </p>
            </div>
            <Switch
              checked={f.con_fattura}
              onCheckedChange={set('con_fattura')}
              data-testid="costo-fisso-con-fattura"
            />
          </div>

          <div>
            <Label className="text-xs">Note</Label>
            <Textarea
              rows={2}
              value={f.note}
              onChange={(e) => set('note')(e.target.value)}
              placeholder="Contratto, IBAN, riferimenti…"
              data-testid="costo-fisso-note"
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <Label className="text-xs">Costo attivo (conteggiato nei costi totali)</Label>
            <Switch checked={f.attivo} onCheckedChange={set('attivo')} data-testid="costo-fisso-attivo" />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onChiudi} data-testid="costo-fisso-annulla">
            Annulla
          </Button>
          <Button onClick={invia} disabled={!valido || salvando} data-testid="costo-fisso-salva">
            {salvando ? 'Salvataggio…' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CostoFissoForm;
